/**
 * How a comment stays attached to a piece of a page.
 *
 * The governing rule for everything in this file: a comment drawn in the wrong
 * place is worse than a comment we admit we cannot place. Every anchor therefore
 * carries several independent ways to find its target, and the resolver reports
 * failure rather than falling back to a plausible guess.
 */

export type AnchorKind = 'pin' | 'text' | 'region';

/**
 * Several routes to the same element, tried in order. `primary` is the most
 * stable expression we could generate; `fallbacks` are progressively looser.
 */
export interface SelectorSet {
  primary: string;
  fallbacks: string[];
}

/**
 * The nearest enclosing `data-annotate-id`, captured on every anchor.
 *
 * This is the join between a comment and the source that produced it. A selector
 * describes where something sits in rendered HTML, which is no help to anyone
 * holding a repository of templates — but a `data-annotate-id` is written by
 * hand, survives regeneration, and can be grepped for. It is what lets an agent
 * be told "fix this" and find the file.
 *
 * Null when the page carries no such ids, which is why the conventions doc exists
 * and why the skill seeds it. Without one, the fallbacks are the page URL and the
 * quoted text — usually enough to grep for on a content site, and considerably
 * less reliable on a generated one.
 */
export type SectionId = string | null;

/** A click somewhere on the page. Offsets are fractions of the target element's
 *  box (0..1) rather than pixels, so the marker survives a reflow at a different
 *  viewport width. */
export interface PinAnchor {
  kind: 'pin';
  /** Nearest enclosing `data-annotate-id`. See SectionId. */
  sectionId?: SectionId;
  selectors: SelectorSet;
  offsetPct: { x: number; y: number };
  /** Text near the click, used to sanity-check a resolved element and to give an
   *  orphaned comment something human-readable to show. */
  context: string;
}

/** A selected phrase. The quote plus its surrounding text is the most durable
 *  anchor we have: it survives the element being moved, renamed or re-nested. */
export interface TextAnchor {
  kind: 'text';
  /** Nearest enclosing `data-annotate-id`. See SectionId. */
  sectionId?: SectionId;
  /** Common ancestor of the selection — a hint, not the source of truth. */
  selectors: SelectorSet;
  quote: string;
  prefix: string;
  suffix: string;
}

/** A dragged box, for images and whole sections. Stored as fractions of the
 *  common ancestor's box for the same reflow reason as pins. */
export interface RegionAnchor {
  kind: 'region';
  /** Nearest enclosing `data-annotate-id`. See SectionId. */
  sectionId?: SectionId;
  selectors: SelectorSet;
  rectPct: { x: number; y: number; w: number; h: number };
  context: string;
}

export type Anchor = PinAnchor | TextAnchor | RegionAnchor;

export const ANCHOR_KINDS: readonly AnchorKind[] = ['pin', 'text', 'region'];

/** How many characters of context we keep either side of a text selection.
 *  Enough to disambiguate repeated phrases, short enough to stay cheap. */
export const TEXT_CONTEXT_LEN = 32;

export function isAnchor(value: unknown): value is Anchor {
  if (!value || typeof value !== 'object') return false;
  const a = value as Partial<Anchor>;
  if (!a.kind || !ANCHOR_KINDS.includes(a.kind)) return false;
  const sel = (a as { selectors?: SelectorSet }).selectors;
  if (!sel || typeof sel.primary !== 'string' || !Array.isArray(sel.fallbacks)) return false;

  switch (a.kind) {
    case 'pin': {
      const p = a as PinAnchor;
      return isFraction(p.offsetPct?.x) && isFraction(p.offsetPct?.y);
    }
    case 'text': {
      const t = a as TextAnchor;
      return typeof t.quote === 'string' && t.quote.length > 0;
    }
    case 'region': {
      const r = a as RegionAnchor;
      return (
        isFraction(r.rectPct?.x) &&
        isFraction(r.rectPct?.y) &&
        isFraction(r.rectPct?.w) &&
        isFraction(r.rectPct?.h)
      );
    }
  }
}

function isFraction(n: unknown): n is number {
  return typeof n === 'number' && Number.isFinite(n) && n >= -1 && n <= 2;
}

/** A one-line human description of what a comment is attached to. Shown in the
 *  sidebar, in the orphan tray, and in the ClickUp ticket body. */
export function describeAnchor(anchor: Anchor): string {
  switch (anchor.kind) {
    case 'text':
      return `“${truncate(anchor.quote, 120)}”`;
    case 'region':
      return anchor.context ? `region near “${truncate(anchor.context, 80)}”` : 'a region of the page';
    case 'pin':
      return anchor.context ? `near “${truncate(anchor.context, 80)}”` : 'a point on the page';
  }
}

function truncate(s: string, max: number): string {
  const clean = s.replace(/\s+/g, ' ').trim();
  return clean.length > max ? `${clean.slice(0, max - 1)}…` : clean;
}
